'use client';

import * as React from 'react';
import { Track } from 'livekit-client';
import {
  TrackToggle,
  DisconnectButton,
  useLocalParticipantPermissions,
  useRoomContext,
} from '@livekit/components-react';
import { supportsScreenSharing } from '@livekit/components-core';
import { Hand, MoreHorizontal, MonitorUp, UserPlus, Users, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RecordButton } from './RecordButton';
import { useHandRaise } from './HandRaiseContext';
import { useHandRaiseQueue } from './HandRaiseQueueContext';
import { readEconomyModeEnabled } from './economyMode';
import {
  getEffectiveScreenShareQualityProfile,
  getScreenShareCaptureOptions,
  readScreenShareQualityProfile,
} from './screen-share-profile';
import { shouldUseMobileControlBar } from './meeting-toolbar-layout';
import toast from 'react-hot-toast';

const SCREEN_SHARE_UNSUPPORTED_TOAST =
  'Neste dispositivo o navegador não permite compartilhar a tela. No tablet, use o Safari atualizado ou um computador.';

function useIsMobilePortrait() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia('(orientation: portrait)');

    const evaluate = () => {
      setIsMobile(shouldUseMobileControlBar(window.innerWidth, mql.matches));
    };

    evaluate();
    window.addEventListener('resize', evaluate);
    mql.addEventListener('change', evaluate);

    return () => {
      window.removeEventListener('resize', evaluate);
      mql.removeEventListener('change', evaluate);
    };
  }, []);

  return isMobile;
}

export function MobileControlBar() {
  const isMobilePortrait = useIsMobilePortrait();
  const permissions = useLocalParticipantPermissions();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [inviteUrl, setInviteUrl] = React.useState('');
  const optionsButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const room = useRoomContext();
  const { handRaisedParticipants, setHandRaised } = useHandRaise();
  const { addToQueue, removeFromQueue, getQueuePosition } = useHandRaiseQueue();

  const localParticipantId = room?.localParticipant?.identity ?? null;
  const isHandRaised = localParticipantId ? handRaisedParticipants.has(localParticipantId) : false;
  const queuePosition = localParticipantId ? getQueuePosition(localParticipantId) : -1;

  React.useEffect(() => {
    const container = document.querySelector('.lk-room-container');
    const controlBar = document.querySelector('.lk-control-bar');
    if (!container) return;

    if (isMobilePortrait) {
      container.setAttribute('data-mobile-controls', 'true');
      controlBar?.setAttribute('data-hidden-mobile', 'true');
    } else {
      container.removeAttribute('data-mobile-controls');
      controlBar?.removeAttribute('data-hidden-mobile');
      setIsMenuOpen(false);
      setInviteOpen(false);
    }

    return () => {
      container.removeAttribute('data-mobile-controls');
      controlBar?.removeAttribute('data-hidden-mobile');
    };
  }, [isMobilePortrait]);

  React.useEffect(() => {
    if (!isMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        !optionsButtonRef.current?.contains(target)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isMenuOpen]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    setInviteUrl(window.location.href);
  }, []);

  const [sheetOpen, setSheetOpen] = React.useState(false);

  const screenShareCaptureOptions = React.useMemo(() => {
    const economy = readEconomyModeEnabled();
    const profile = getEffectiveScreenShareQualityProfile(
      readScreenShareQualityProfile(),
      economy,
    );
    return getScreenShareCaptureOptions(profile, economy ? 'economy' : 'default');
  }, [sheetOpen]);

  const closeMenu = React.useCallback(() => setIsMenuOpen(false), []);

  const openInviteDialog = React.useCallback(() => {
    closeMenu();
    setInviteOpen(true);
  }, [closeMenu]);

  const openParticipants = React.useCallback(() => {
    closeMenu();
    const participantsToggle = document.querySelector(
      '.lk-control-bar .lk-participants-toggle',
    ) as HTMLButtonElement | null;
    participantsToggle?.click();
  }, [closeMenu]);

  const mergeLocalParticipantMetadata = React.useCallback(
    async (updates: Record<string, unknown>) => {
      const participant = room?.localParticipant;
      if (!participant) {
        return;
      }

      if (participant.permissions?.canUpdateMetadata === false) {
        return;
      }

      const currentMetadata = participant.metadata;
      let parsed: Record<string, any> = {};

      if (currentMetadata) {
        try {
          const candidate = JSON.parse(currentMetadata);
          if (candidate && typeof candidate === 'object') {
            parsed = candidate as Record<string, any>;
          }
        } catch (error) {
          console.warn(
            'MobileControlBar: não foi possível interpretar metadata atual do participante local:',
            error,
          );
        }
      }

      const merged = {
        ...parsed,
        ...updates,
      };

      try {
        const serialized = JSON.stringify(merged);
        if (serialized !== currentMetadata) {
          await participant.setMetadata(serialized);
        }
      } catch (error) {
        console.warn('MobileControlBar: erro ao atualizar metadata do participante local:', error);
      }
    },
    [room],
  );

  const publishHandRaiseViaDataChannel = React.useCallback(
    (raised: boolean) => {
      try {
        const data = {
          type: 'hand-raise',
          raised,
          id: room?.localParticipant?.identity,
          timestamp: Date.now(),
        };

        room?.localParticipant
          ?.publishData(new TextEncoder().encode(JSON.stringify(data)), { topic: 'hand-raise' })
          .catch((error) => {
            console.error('Erro ao enviar data channel:', error);
          });
      } catch (error) {
        console.error('Erro ao publicar via data channel:', error);
      }
    },
    [room],
  );

  const handleHandRaiseToggle = React.useCallback(() => {
    if (!room?.localParticipant || !localParticipantId) return;

    const next = !isHandRaised;

    // Atualizar o contexto compartilhado
    setHandRaised(localParticipantId, next);

    // Gerenciar fila de mãos levantadas
    const participantName = room.localParticipant.name || room.localParticipant.identity;
    if (next) {
      addToQueue(localParticipantId, participantName);
    } else {
      removeFromQueue(localParticipantId);
    }

    // Publicar via data channel
    publishHandRaiseViaDataChannel(next);

    // Atualizar metadata
    void mergeLocalParticipantMetadata({ handRaised: next, timestamp: Date.now() });

    closeMenu();
  }, [
    room,
    localParticipantId,
    isHandRaised,
    setHandRaised,
    addToQueue,
    removeFromQueue,
    publishHandRaiseViaDataChannel,
    mergeLocalParticipantMetadata,
    closeMenu,
  ]);

  if (!isMobilePortrait || !permissions) {
    return null;
  }

  const canPublishSource = (source: Track.Source) => {
    if (!permissions.canPublish) return false;
    if (!permissions.canPublishSources || permissions.canPublishSources.length === 0) return true;
    const mapping: Record<Track.Source, number> = {
      [Track.Source.Camera]: 1,
      [Track.Source.Microphone]: 2,
      [Track.Source.ScreenShare]: 3,
      [Track.Source.ScreenShareAudio]: 4,
      [Track.Source.Unknown]: 0,
    };
    return permissions.canPublishSources.includes(mapping[source]);
  };

  const micEnabled = canPublishSource(Track.Source.Microphone);
  const camEnabled = canPublishSource(Track.Source.Camera);
  const canShareScreen = canPublishSource(Track.Source.ScreenShare) && supportsScreenSharing();
  const isMuted = !room?.localParticipant?.isMicrophoneEnabled;
  const isCamOff = !room?.localParticipant?.isCameraEnabled;

  return (
    <>
      {/* Keep original lk-mobile-control-bar class for DOM attribute management */}
      <div className="lk-mobile-control-bar flex items-center justify-center gap-1.5 px-5 pb-5 pt-2">

        {/* DOCK COMPACTO */}
        <div className="flex items-center gap-[3px] px-2 py-[7px] rounded-[18px] bg-[rgba(8,8,20,0.92)] backdrop-blur-xl border border-white/[0.09] shadow-[0_6px_24px_rgba(0,0,0,0.5)]">

          {/* Mic */}
          {micEnabled && (
            <div className={cn(
              "w-[42px] h-[42px] rounded-[11px] flex items-center justify-center cursor-pointer border",
              isMuted ? "bg-red-500/10 border-red-500/20" : "bg-white/[0.05] border-white/[0.07]"
            )}>
              <TrackToggle source={Track.Source.Microphone} showIcon />
            </div>
          )}

          {/* Camera */}
          {camEnabled && (
            <div className={cn(
              "w-[42px] h-[42px] rounded-[11px] flex items-center justify-center cursor-pointer border",
              isCamOff ? "bg-red-500/10 border-red-500/20" : "bg-white/[0.05] border-white/[0.07]"
            )}>
              <TrackToggle source={Track.Source.Camera} showIcon />
            </div>
          )}

          <div className="w-px h-6 bg-white/[0.07] mx-0.5" />

          {/* Compartilhar tela — visível no dock (tablet não esconde no ⋯) */}
          <div
            className={cn(
              "w-[42px] h-[42px] rounded-[11px] flex items-center justify-center border",
              canShareScreen
                ? "bg-white/[0.05] border-white/[0.07] cursor-pointer"
                : "bg-white/[0.03] border-white/[0.05] cursor-not-allowed opacity-50",
            )}
            title={canShareScreen ? 'Compartilhar tela' : SCREEN_SHARE_UNSUPPORTED_TOAST}
            onClick={() => {
              if (!canShareScreen) {
                toast.error(SCREEN_SHARE_UNSUPPORTED_TOAST, { duration: 8_000 });
              }
            }}
          >
            {canShareScreen ? (
              <TrackToggle
                source={Track.Source.ScreenShare}
                showIcon={false}
                captureOptions={screenShareCaptureOptions}
              >
                <MonitorUp size={18} className="text-white/70" />
              </TrackToggle>
            ) : (
              <MonitorUp size={18} className="text-white/40" aria-hidden="true" />
            )}
          </div>

          {/* Gravar — visível no dock para anfitrião */}
          <div
            className="w-[42px] h-[42px] rounded-[11px] bg-white/[0.05] border border-white/[0.07] flex items-center justify-center"
            title="Gravar reunião"
          >
            <RecordButton inline />
          </div>

          {/* ⋯ Mais */}
          <div
            onClick={() => setSheetOpen(true)}
            className="w-[42px] h-[42px] rounded-[11px] bg-white/[0.05] border border-white/[0.07] flex items-center justify-center cursor-pointer"
          >
            <MoreHorizontal size={18} className="text-white/50" />
          </div>
        </div>

        {/* Sair */}
        <div className="w-[50px] h-[42px] rounded-[14px] bg-[#dc2626] flex items-center justify-center cursor-pointer shadow-[0_4px_16px_rgba(220,38,38,0.32)] hover:bg-[#e53535] transition-colors">
          <DisconnectButton aria-label="Sair da reunião">
            <span className="sr-only">Sair</span>
          </DisconnectButton>
        </div>

        {/* BOTTOM SHEET "Mais opções" */}
        {sheetOpen && (
          <>
            <div onClick={() => setSheetOpen(false)} className="fixed inset-0 z-30 bg-black/55" />
            <div className="fixed bottom-0 left-0 right-0 z-[31] bg-[#0e0e1c] rounded-t-[24px] border border-white/[0.08] border-b-0 px-4 pb-6 pt-4 [animation:em-sheet-up_0.26s_cubic-bezier(0.34,1.2,0.64,1)]">
              <div className="w-9 h-1 rounded-full bg-white/10 mx-auto mb-5" />
              <div className="grid grid-cols-3 gap-2.5">

                <div
                  className={cn(
                    "flex flex-col items-center gap-2 py-3.5 px-2 rounded-2xl border transition-colors",
                    canShareScreen
                      ? "bg-white/[0.04] border-white/[0.07] hover:bg-white/[0.08] cursor-pointer"
                      : "bg-white/[0.03] border-white/[0.05] opacity-60 cursor-not-allowed",
                  )}
                  onClick={() => {
                    if (!canShareScreen) {
                      toast.error(SCREEN_SHARE_UNSUPPORTED_TOAST, { duration: 8_000 });
                    }
                  }}
                >
                  {canShareScreen ? (
                    <TrackToggle
                      source={Track.Source.ScreenShare}
                      showIcon={false}
                      captureOptions={screenShareCaptureOptions}
                      onChange={(enabled, isUserInitiated) => {
                        if (isUserInitiated) setSheetOpen(false);
                      }}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <MonitorUp size={20} className="text-white/50" />
                        <span className="text-[11px] text-center leading-tight text-[#6868a0]">Compartilhar Tela</span>
                      </div>
                    </TrackToggle>
                  ) : (
                    <>
                      <MonitorUp size={20} className="text-white/40" />
                      <span className="text-[11px] text-center leading-tight text-[#6868a0]">Compartilhar Tela</span>
                    </>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const defaultToggle = document.querySelector(
                      '.lk-control-bar .lk-chat-toggle',
                    ) as HTMLButtonElement | null;
                    defaultToggle?.click();
                    setSheetOpen(false);
                  }}
                  className="flex flex-col items-center gap-2 py-3.5 px-2 rounded-2xl cursor-pointer border transition-colors bg-white/[0.04] border-white/[0.07] hover:bg-white/[0.08]"
                >
                  <MessageSquare size={20} className="text-white/50" />
                  <span className="text-[11px] text-center leading-tight text-[#6868a0]">Chat</span>
                </button>

                <button
                  type="button"
                  onClick={() => { openParticipants(); setSheetOpen(false); }}
                  className="flex flex-col items-center gap-2 py-3.5 px-2 rounded-2xl cursor-pointer border transition-colors bg-white/[0.04] border-white/[0.07] hover:bg-white/[0.08]"
                >
                  <Users size={20} className="text-white/50" />
                  <span className="text-[11px] text-center leading-tight text-[#6868a0]">Participantes</span>
                </button>

                <button
                  type="button"
                  onClick={() => { handleHandRaiseToggle(); setSheetOpen(false); }}
                  className={cn(
                    "flex flex-col items-center gap-2 py-3.5 px-2 rounded-2xl cursor-pointer border transition-colors",
                    isHandRaised
                      ? "bg-amber-500/[0.08] border-amber-500/[0.18]"
                      : "bg-white/[0.04] border-white/[0.07] hover:bg-white/[0.08]"
                  )}
                >
                  <Hand size={20} className={isHandRaised ? "text-amber-400" : "text-white/50"} />
                  <span className={cn("text-[11px] text-center leading-tight", isHandRaised ? "text-amber-400" : "text-[#6868a0]")}>
                    {isHandRaised ? 'Baixar mão' : 'Levantar mão'}
                  </span>
                </button>

                <div className="bg-red-500/[0.07] border border-red-500/[0.14] hover:bg-red-500/[0.12] flex flex-col items-center gap-2 py-3.5 px-2 rounded-2xl cursor-pointer transition-colors">
                  <RecordButton inline />
                  <span className="text-[11px] text-center leading-tight text-red-400">Gravar</span>
                </div>

                <button
                  type="button"
                  onClick={() => { openInviteDialog(); setSheetOpen(false); }}
                  className="flex flex-col items-center gap-2 py-3.5 px-2 rounded-2xl cursor-pointer border transition-colors bg-white/[0.04] border-white/[0.07] hover:bg-white/[0.08]"
                >
                  <UserPlus size={20} className="text-white/50" />
                  <span className="text-[11px] text-center leading-tight text-[#6868a0]">Convidar</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} inviteUrl={inviteUrl} />
    </>
  );
}

type InviteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inviteUrl: string;
};

function InviteDialog({ open, onOpenChange, inviteUrl }: InviteDialogProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar link de convite', error);
    }
  }, [inviteUrl]);

  const handleShareEmail = React.useCallback(() => {
    const subject = encodeURIComponent('Convite para reunião');
    const body = encodeURIComponent(`Participe da reunião através do link: ${inviteUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }, [inviteUrl]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="lk-mobile-invite-dialog">
        <DialogHeader>
          <DialogTitle>Convidar pessoas</DialogTitle>
          <DialogDescription>
            Compartilhe o link abaixo para convidar outras pessoas.
          </DialogDescription>
        </DialogHeader>
        <div className="lk-mobile-invite-link">
          <Input value={inviteUrl} readOnly />
          <Button variant="secondary" onClick={handleCopy} className="lk-mobile-invite-copy">
            {copied ? 'Copiado!' : 'Copiar link'}
          </Button>
        </div>
        <Button variant="ghost" onClick={handleShareEmail} className="lk-mobile-invite-email">
          Enviar por e-mail
        </Button>
      </DialogContent>
    </Dialog>
  );
}
